import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { isEqual, omit } from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import rubricService from 'services/rubric';
import { Button, Stack } from 'shell/components';
import { RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getWordCount, isNatural } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { rubrics, setRubrics } = useApp();
    const { setNode } = useAppBar();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const params = useParams();
    const { message } = useSnackbar();

    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const rubric = rubrics?.find(({ id }) => id === params.id);

    const validateTitle = useCallback(() => {
        const error = form.title.trim() ? '' : 'Please enter a name';
        setErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [form?.title]);

    const validateCriteria = useCallback(() => {
        let error = false;
        form.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isNatural(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? 'Please enter a number' : '',
                                description: isDescriptionInvalid ? 'Word count exceeded' : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [form?.criteria]);

    const validateAll = useCallback(() => {
        const isTitleValid = validateTitle();
        const areCriteriaValid = validateCriteria();
        if (isTitleValid && areCriteriaValid) return true;
        return false;
    }, [validateTitle, validateCriteria]);

    const onClickCancel = useCallback(() => {
        if (isEqual(rubric.rubric, form)) {
            history.push(`/rubrics/${rubric.id}`);
        } else {
            setDialog({
                open: true,
                message: 'You Have Unsaved Changes',
                description: 'If you exit without saving, all unsaved changes will be lost.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Exit',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    history.push(`/rubrics/${rubric.id}`);
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    }, [closeDialog, form, history, rubric.id, rubric.rubric, setDialog]);

    const onClickUpdate = useCallback(() => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            const timestamp = new Date().getTime();
            const updatedRubric = { ...omit(rubric, ['_id']), modified_on: timestamp, rubric: form };
            setRubrics((current) => {
                return current.map((r) => {
                    return r.id === updatedRubric.id ? updatedRubric : r;
                });
            });
            rubricService.updateRubric(updatedRubric.id, updatedRubric);
            history.push(`/rubrics/${updatedRubric.id}`);
        } else {
            message.error('One or more fields are not valid.');
        }
    }, [form, history, message, rubric, setRubrics, validateAll]);

    useEffect(() => {
        if (!form) return;

        setNode(
            <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                <Button color={'neutral'} variant={'tertiary'} onClick={onClickCancel}>
                    Cancel
                </Button>
                <Button onClick={onClickUpdate}>Save Changes</Button>
            </Stack>
        );

        return () => setNode(null);
    }, [form, onClickCancel, onClickUpdate, setNode]);

    useEffect(() => {
        if (!rubric) return;
        setForm(rubric.rubric);
        setErrors({
            title: '',
            criteria: {},
        });
    }, [rubric]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = true;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    useEffect(() => {
        const onBackButtonEvent = (e) => {
            e.preventDefault();
            if (window.confirm('Are you sure you want to go back? Any unsaved progress will be lost.')) {
                window.history.back();
            } else {
                window.history.pushState(null, null, window.location.pathname);
            }
        };

        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', onBackButtonEvent);

        return () => {
            window.removeEventListener('popstate', onBackButtonEvent);
        };
    }, []);

    return (
        <PageContext.Provider
            value={{
                form,
                setForm,
                errors,
                setErrors,
                hasClickedSubmit,
                validateTitle,
                validateCriteria,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
